import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vendorAPI } from '../../../api/vendor';
import {
    ArrowLeft, Package, Clock, CheckCircle, XCircle, AlertCircle, FileText,
} from 'lucide-react';
import { formatDateTimeEnglish } from '../../../utils/date';
import { formatPrice } from '../../../utils/utilityFunctions';
import toast from '../../../utils/toast';
import type { ReturnRequest } from '../../../types';

const VendorReturnDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [r, setR] = useState<ReturnRequest | null>(null);
    const [loading, setLoading] = useState(true);

    // decision state
    const [decision, setDecision] = useState<'approve' | 'partial' | 'reject'>('approve');
    const [partialQty, setPartialQty] = useState(1);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const data = await vendorAPI.getReturn(id);
                setR(data);
                setPartialQty(Math.max(1, data.requested_quantity - 1));
            } catch {
                toast.error('Return not found');
                navigate('/vendor/returns');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    const handleSubmit = async () => {
        if (!r) return;
        setSubmitting(true);
        setError('');
        try {
            const payload: any = { decision, notes: notes.trim() || undefined };
            if (decision === 'partial') payload.approved_quantity = partialQty;
            const updated = await vendorAPI.decideReturn(r.id, payload);
            setR(updated);
            toast.success(
                decision === 'reject' ? 'Return rejected' : 'Return approved'
            );
        } catch (e: any) {
            const msg = e?.response?.data?.error || 'Failed to submit decision';
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !r) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const isPending = r.status === 'pending';
    const isDecided = !isPending;
    const partialMax = r.requested_quantity - 1;
    const partialValid = decision !== 'partial' || (partialQty >= 1 && partialQty <= partialMax);

    const isReplacement = r.kind === 'replacement';
    const approveLabel = isReplacement ? 'Approve replacement' : 'Approve full refund';
    const approveDescription = isReplacement
        ? `Ship replacement and collect original (${formatPrice(r.item_refund_amount)} value)`
        : `${formatPrice(r.item_refund_amount)} back to customer`;
    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                <button
                    onClick={() => navigate('/vendor/returns')}
                    className="flex items-center text-blue-600 hover:text-blue-700 font-medium mb-4"
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Back to Returns
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left column: request details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Product */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0">
                                    {r.product_image ? (
                                        <img src={r.product_image} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <Package className="w-7 h-7 text-gray-400" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-xl font-bold text-gray-900 break-words">
                                        {r.product_name}
                                    </h1>
                                    {r.product_variant_name && (
                                        <p className="text-sm text-gray-600">{r.product_variant_name}</p>
                                    )}
                                    <p className="text-sm text-gray-500 mt-1">
                                        Order #{r.order_number}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Requested</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize mt-1">{r.kind}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Quantity</p>
                                    <p className="text-sm font-medium text-gray-900 mt-1">{r.requested_quantity}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">Refund amount</p>
                                    <p className="text-sm font-medium text-gray-900 mt-1">
                                        {formatPrice(r.item_refund_amount)}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Reason */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6">
                            <h2 className="font-semibold text-gray-900 mb-3">Customer's reason</h2>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.reason}</p>
                        </div>

                        {/* Evidence */}
                        {r.evidence && r.evidence.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xl p-6">
                                <h2 className="font-semibold text-gray-900 mb-3">Evidence</h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                    {r.evidence.map(ev => (
                                        <a
                                            key={ev.id}
                                            href={ev.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block aspect-square bg-gray-100 rounded-lg overflow-hidden hover:opacity-80"
                                        >
                                            {ev.kind === 'image' ? (
                                                <img src={ev.url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <video src={ev.url} className="w-full h-full object-cover" />
                                            )}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Already-decided view */}
                        {isDecided && (
                            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-medium text-gray-900 capitalize">
                                            {r.status.replace(/_/g, ' ')}
                                        </p>
                                        {r.approved_amount && (
                                            <p className="text-sm text-gray-700 mt-1">
                                                Approved amount: {formatPrice(r.approved_amount)}
                                                {r.approved_quantity && r.approved_quantity !== r.requested_quantity && (
                                                    <> ({r.approved_quantity} of {r.requested_quantity})</>
                                                )}
                                            </p>
                                        )}
                                        {r.vendor_notes && (
                                            <p className="text-sm text-gray-700 mt-2">
                                                Your notes: {r.vendor_notes}
                                            </p>
                                        )}
                                        {r.vendor_decided_at && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Decided {formatDateTimeEnglish(r.vendor_decided_at)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right column: decision form / info */}
                    <div className="space-y-4">
                        {isPending ? (
                            <div className="bg-white border border-gray-200 rounded-xl p-6">
                                <div className="flex items-center gap-2 mb-4">
                                    <Clock className="w-4 h-4 text-amber-600" />
                                    <p className="text-xs text-amber-700 font-medium">
                                        Respond by {formatDateTimeEnglish(r.sla_deadline)}
                                    </p>
                                </div>

                                <h2 className="font-semibold text-gray-900 mb-3">Your decision</h2>

                                <div className="space-y-2">
                                    <DecisionOption
                                        value="approve"
                                        current={decision}
                                        onSelect={setDecision}
                                        label={approveLabel}
                                        description={approveDescription}
                                        icon={CheckCircle}
                                    />
                                    {!isReplacement && partialMax >= 1 && (
                                        <DecisionOption
                                            value="partial"
                                            current={decision}
                                            onSelect={setDecision}
                                            label="Partially approve"
                                            description="Only part of the request"
                                            icon={AlertCircle}
                                        />
                                    )}
                                    <DecisionOption
                                        value="reject"
                                        current={decision}
                                        onSelect={setDecision}
                                        label={isReplacement ? 'Reject replacement' : 'Reject refund request'}
                                        description="No refund will be issued"
                                        icon={XCircle}
                                    />
                                </div>

                                {decision === 'partial' && (
                                    <div className="mt-4">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Approve quantity
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={partialMax}
                                            value={partialQty}
                                            onChange={e =>
                                                setPartialQty(
                                                    Math.max(1, Math.min(partialMax, Number(e.target.value)))
                                                )
                                            }
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Refund: {formatPrice(
                                                (parseFloat(r.item_refund_amount) / r.requested_quantity * partialQty).toFixed(2)
                                            )}
                                        </p>
                                    </div>
                                )}

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        value={notes}
                                        onChange={e => setNotes(e.target.value)}
                                        rows={3}
                                        placeholder="Explain your decision to the customer…"
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                    />
                                </div>

                                {error && (
                                    <p className="mt-3 text-sm text-red-600">{error}</p>
                                )}

                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting || !partialValid}
                                    className="mt-4 w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {submitting ? 'Submitting…' : 'Submit decision'}
                                </button>
                            </div>
                        ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                                <FileText className="w-6 h-6 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-600">
                                    This return has been decided. No further action needed.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const DecisionOption: React.FC<{
    value: 'approve' | 'partial' | 'reject';
    current: string;
    onSelect: (v: any) => void;
    label: string;
    description: string;
    icon: React.ElementType;
}> = ({ value, current, onSelect, label, description, icon: Icon }) => {
    const selected = current === value;
    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={`w-full flex items-start gap-3 p-3 border rounded-lg text-left ${selected
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50'
                }`}
        >
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${selected ? 'text-blue-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
            </div>
        </button>
    );
};

export default VendorReturnDetail;